import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRightLeft, CheckCircle, X, AlertTriangle, Package,
  Calendar, MapPin, User, FileText, Download,
  Upload, Plus, Search, Filter, RefreshCw, Eye,
  Edit, Trash2, MoreHorizontal, ChevronRight,
  Clock, DollarSign, Scale, Box, Camera, QrCode,
  Barcode, Scan, Printer, Bell, UserCheck, Store,
  Home, Database, PieChart, LineChart, BarChart2,
  Truck, Building2, Users, Target, Route
} from 'lucide-react';

const TransferManagement = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFromLocation, setSelectedFromLocation] = useState('all');
  const [selectedToLocation, setSelectedToLocation] = useState('all');
  const [showCamera, setShowCamera] = useState(false);
  const [scanningMode, setScanningMode] = useState('barcode');

  // Mock data for transfers
  const transferData = [
    {
      id: 1,
      transferNumber: 'TRF-2024-001',
      fromLocation: 'Warehouse A',
      toLocation: 'Store 1',
      status: 'pending',
      requestedDate: '2024-01-15',
      scheduledDate: '2024-01-20',
      completedDate: null,
      items: [
        { sku: 'INV-001', name: 'Premium Coffee Beans', quantity: 50, unit: 'kg', barcode: '1234567890123' },
        { sku: 'INV-005', name: 'Premium Tea Selection', quantity: 25, unit: 'boxes', barcode: '1234567890127' }
      ],
      totalValue: 1625.00,
      requestedBy: 'John Smith',
      assignedTo: 'Mike Wilson',
      priority: 'high',
      notes: 'Urgent transfer for weekend sale',
      images: ['transfer-001-1.jpg', 'transfer-001-2.jpg'],
      documents: ['transfer-request-001.pdf', 'approval-001.pdf'],
      route: 'Warehouse A → Store 1',
      estimatedTime: '2 hours',
      vehicle: 'Truck-001',
      driver: 'David Lee'
    },
    {
      id: 2,
      transferNumber: 'TRF-2024-002',
      fromLocation: 'Cold Storage',
      toLocation: 'Store 2',
      status: 'in-transit',
      requestedDate: '2024-01-14',
      scheduledDate: '2024-01-18',
      completedDate: null,
      items: [
        { sku: 'INV-002', name: 'Organic Milk', quantity: 40, unit: 'liters', barcode: '1234567890124' }
      ],
      totalValue: 128.00,
      requestedBy: 'Sarah Johnson',
      assignedTo: 'Mike Wilson',
      priority: 'medium',
      notes: 'Temperature controlled transfer',
      images: ['transfer-002-1.jpg'],
      documents: ['transfer-request-002.pdf'],
      route: 'Cold Storage → Store 2',
      estimatedTime: '1.5 hours',
      vehicle: 'Refrigerated Van-002',
      driver: 'Lisa Brown'
    },
    {
      id: 3,
      transferNumber: 'TRF-2024-003',
      fromLocation: 'Bakery Section',
      toLocation: 'Store 3',
      status: 'completed',
      requestedDate: '2024-01-13',
      scheduledDate: '2024-01-15',
      completedDate: '2024-01-15',
      items: [
        { sku: 'INV-003', name: 'Artisan Bread', quantity: 50, unit: 'pieces', barcode: '1234567890125' }
      ],
      totalValue: 125.00,
      requestedBy: 'Mike Wilson',
      assignedTo: 'Mike Wilson',
      priority: 'low',
      notes: 'Daily bread transfer completed successfully',
      images: ['transfer-003-1.jpg', 'transfer-003-2.jpg'],
      documents: ['transfer-request-003.pdf', 'completion-003.pdf'],
      route: 'Bakery Section → Store 3',
      estimatedTime: '1 hour',
      vehicle: 'Van-003',
      driver: 'Alex Chen'
    },
    {
      id: 4,
      transferNumber: 'TRF-2024-004',
      fromLocation: 'Produce Section',
      toLocation: 'Store 4',
      status: 'partial',
      requestedDate: '2024-01-16',
      scheduledDate: '2024-01-17',
      completedDate: '2024-01-17',
      items: [
        { sku: 'INV-004', name: 'Fresh Vegetables Mix', quantity: 30, unit: 'kg', barcode: '1234567890126' }
      ],
      totalValue: 144.00,
      requestedBy: 'Lisa Brown',
      assignedTo: 'Mike Wilson',
      priority: 'high',
      notes: 'Partial transfer due to vehicle breakdown',
      images: ['transfer-004-1.jpg', 'damage-report-004.jpg'],
      documents: ['transfer-request-004.pdf', 'incident-report-004.pdf'],
      route: 'Produce Section → Store 4',
      estimatedTime: '1.5 hours',
      vehicle: 'Truck-004',
      driver: 'Robert Kim'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500 text-white';
      case 'approved': return 'bg-blue-500 text-white';
      case 'in-transit': return 'bg-purple-500 text-white';
      case 'completed': return 'bg-green-500 text-white';
      case 'partial': return 'bg-orange-500 text-white';
      case 'cancelled': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'in-transit': return <Truck className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'partial': return <AlertTriangle className="w-4 h-4" />;
      case 'cancelled': return <X className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredData = transferData.filter(item => {
    const matchesSearch = item.transferNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.fromLocation.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.toLocation.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFromLocation = selectedFromLocation === 'all' || item.fromLocation === selectedFromLocation;
    const matchesToLocation = selectedToLocation === 'all' || item.toLocation === selectedToLocation;
    const matchesStatus = activeTab === 'all' || item.status === activeTab;
    return matchesSearch && matchesFromLocation && matchesToLocation && matchesStatus;
  });

  const tabs = [
    { id: 'all', label: 'All', count: transferData.length },
    { id: 'pending', label: 'Pending', count: transferData.filter(item => item.status === 'pending').length },
    { id: 'approved', label: 'Approved', count: transferData.filter(item => item.status === 'approved').length },
    { id: 'in-transit', label: 'In Transit', count: transferData.filter(item => item.status === 'in-transit').length },
    { id: 'completed', label: 'Completed', count: transferData.filter(item => item.status === 'completed').length },
    { id: 'partial', label: 'Partial', count: transferData.filter(item => item.status === 'partial').length },
    { id: 'cancelled', label: 'Cancelled', count: transferData.filter(item => item.status === 'cancelled').length }
  ];

  const locations = [...new Set([
    ...transferData.map(item => item.fromLocation),
    ...transferData.map(item => item.toLocation)
  ])];

  const TransferCard = ({ item }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{item.transferNumber}</h3>
            <p className="text-sm text-gray-600">{item.route}</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(item.status)}`}>
              {getStatusIcon(item.status)}
              <span>{item.status.replace('-', ' ').toUpperCase()}</span>
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
              {item.priority.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Route Information */}
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Building2 className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">{item.fromLocation}</span>
              <ArrowRightLeft className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">{item.toLocation}</span>
            </div>
            <div className="text-right">
              <p className="text-xs text-blue-600">Est. Time: {item.estimatedTime}</p>
              <p className="text-xs text-blue-600">Vehicle: {item.vehicle}</p>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Requested Date</p>
            <p className="text-sm font-medium text-gray-900">{new Date(item.requestedDate).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Scheduled Date</p>
            <p className="text-sm font-medium text-gray-900">{new Date(item.scheduledDate).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Requested By</p>
            <p className="text-sm font-medium text-gray-900">{item.requestedBy}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Value</p>
            <p className="text-sm font-medium text-gray-900">${item.totalValue.toLocaleString()}</p>
          </div>
        </div>

        {/* Items List */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Items</h4>
          <div className="space-y-2">
            {item.items.map((product, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">{product.name}</p>
                  <p className="text-xs text-gray-600">SKU: {product.sku} • Barcode: {product.barcode}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900">{product.quantity} {product.unit}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Driver Information */}
        <div className="mb-4 p-3 bg-green-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-900">Driver: {item.driver}</span>
            </div>
            <div className="text-right">
              <p className="text-xs text-green-600">Vehicle: {item.vehicle}</p>
            </div>
          </div>
        </div>

        {/* Notes */}
        {item.notes && (
          <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
            <p className="text-sm text-yellow-800">{item.notes}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Camera className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <QrCode className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Barcode className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Route className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            {item.status === 'pending' && (
              <button className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                Approve
              </button>
            )}
            {item.status === 'approved' && (
              <button className="px-3 py-1 text-sm bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors">
                Start Transfer
              </button>
            )}
            {item.status === 'in-transit' && (
              <button className="px-3 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                Complete
              </button>
            )}
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      {/* Header with Quick Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Transfer Management</h2>
            <p className="text-gray-600">Manage inventory transfers between locations</p>
          </div>
          <div className="flex items-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCamera(!showCamera)}
              className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2"
            >
              <Camera className="w-4 h-4" />
              Scan Items
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Transfer
            </motion.button>
          </div>
        </div>

        {/* Camera Scanner */}
        {showCamera && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Transfer Scanner</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setScanningMode('barcode')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    scanningMode === 'barcode'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Barcode className="w-4 h-4 inline mr-1" />
                  Barcode
                </button>
                <button
                  onClick={() => setScanningMode('qr')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    scanningMode === 'qr'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <QrCode className="w-4 h-4 inline mr-1" />
                  QR Code
                </button>
              </div>
            </div>
            <div className="bg-black rounded-lg h-64 flex items-center justify-center">
              <div className="text-center text-white">
                <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm opacity-75">
                  {scanningMode === 'barcode' ? 'Barcode Scanner Active' : 'QR Code Scanner Active'}
                </p>
                <p className="text-xs opacity-50 mt-1">Scan items for transfer verification</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Apple-like Tabs */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-gray-100 rounded-xl p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                {tab.label}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Enhanced Search and Filters */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search transfers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white hover:bg-gray-50 transition-colors shadow-sm"
              />
            </div>
            <div className="relative">
              <select
                value={selectedFromLocation}
                onChange={(e) => setSelectedFromLocation(e.target.value)}
                className="w-full px-4 py-3 pr-10 rounded-xl border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white hover:bg-gray-50 transition-colors shadow-sm appearance-none"
              >
                <option value="all">From Location</option>
                {locations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
            </div>
            <div className="relative">
              <select
                value={selectedToLocation}
                onChange={(e) => setSelectedToLocation(e.target.value)}
                className="w-full px-4 py-3 pr-10 rounded-xl border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white hover:bg-gray-50 transition-colors shadow-sm appearance-none"
              >
                <option value="all">To Location</option>
                {locations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
            </div>
            <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center justify-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:scale-105">
              <Filter className="w-4 h-4" />
              Advanced Filters
            </button>
          </div>
        </div>
      </div>

      {/* Transfer Items Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnimatePresence mode="wait">
          {filteredData.map((item) => (
            <TransferCard key={item.id} item={item} />
          ))}
        </AnimatePresence>
      </div>

      {/* Empty State */}
      {filteredData.length === 0 && (
        <div className="text-center py-12">
          <ArrowRightLeft className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No transfer items found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
          <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
            Create New Transfer
          </button>
        </div>
      )}
    </div>
  );
};

export default TransferManagement; 